import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}

export function HoldedImportDialog({ open, onOpenChange }: Props) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<{ nuevos: any[]; coincidencias: any[]; soloRaven: any[] } | null>(null);
  const [selNuevos, setSelNuevos] = useState<Record<string, boolean>>({});
  const [decisionFusion, setDecisionFusion] = useState<Record<string, 'fusionar' | 'vincular' | 'ignorar'>>({});
  const [aplicando, setAplicando] = useState(false);

  const getHoldedNif = (contact: any) => contact?.vat_number || contact?.vatNumber || contact?.code || '';

  const extractErrorMessage = async (error: any, data: any): Promise<string | null> => {
    if (data && (data as any).error) return (data as any).error as string;
    if (!error) return null;
    try {
      const ctx = error.context;
      if (ctx && typeof ctx.json === 'function') {
        const body = await ctx.json();
        if (body?.error) return body.error as string;
      } else if (ctx && typeof ctx.text === 'function') {
        const txt = await ctx.text();
        if (txt) return txt;
      }
    } catch (_) { /* fallthrough */ }
    return error.message || 'Error desconocido';
  };

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    supabase.functions
      .invoke('holded-import-contacts', { body: {} })
      .then(async ({ data: d, error }) => {
        const msg = await extractErrorMessage(error, d);
        if (msg) throw new Error(msg);
        setData(d as any);
        const sel: Record<string, boolean> = {};
        for (const h of (d as any).nuevos) sel[h.id] = true;
        setSelNuevos(sel);
        const dec: Record<string, 'fusionar' | 'vincular' | 'ignorar'> = {};
        for (const c of (d as any).coincidencias) dec[c.holded.id] = 'vincular';
        setDecisionFusion(dec);
      })
      .catch((e) => toast.error(e.message))
      .finally(() => setLoading(false));
  }, [open]);

  const aplicar = async () => {
    if (!data) return;
    setAplicando(true);
    try {
      const importar = data.nuevos.filter((h) => selNuevos[h.id]);
      const fusiones = data.coincidencias
        .filter((c) => decisionFusion[c.holded.id] !== 'ignorar')
        .map((c) => ({
          clienteId: c.cliente.id,
          holded: c.holded,
          fusionar: decisionFusion[c.holded.id] === 'fusionar',
        }));
      const { data: res, error } = await supabase.functions.invoke('holded-apply-import', {
        body: { importar, fusiones },
      });
      const msg = await extractErrorMessage(error, res);
      if (msg) throw new Error(msg);
      const r = res as any;
      toast.success(`Importación: ${r.creados} creados, ${r.fusionados} fusionados, ${r.vinculados} vinculados`);
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setAplicando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Importar clientes de Holded</DialogTitle>
          <DialogDescription>
            Compara contactos de Holded con tu base de clientes y aplica la selección.
          </DialogDescription>
        </DialogHeader>
        {loading || !data ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <Tabs defaultValue="nuevos">
            <TabsList>
              <TabsTrigger value="nuevos">Nuevos ({data.nuevos.length})</TabsTrigger>
              <TabsTrigger value="coincidencias">Coincidencias ({data.coincidencias.length})</TabsTrigger>
              <TabsTrigger value="raven">Solo en Raven ({data.soloRaven.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="nuevos" className="space-y-2">
              {data.nuevos.length === 0 && <p className="text-sm text-muted-foreground">Sin novedades.</p>}
              {data.nuevos.map((h) => (
                <div key={h.id} className="flex items-center gap-3 p-2 border rounded">
                  <Checkbox
                    checked={!!selNuevos[h.id]}
                    onCheckedChange={(v) => setSelNuevos((s) => ({ ...s, [h.id]: !!v }))}
                  />
                  <div className="flex-1">
                    <div className="font-medium">{h.name}</div>
                    <div className="text-xs text-muted-foreground">{getHoldedNif(h) || 'sin NIF'} · {h.email || 'sin email'}</div>
                  </div>
                </div>
              ))}
            </TabsContent>

            <TabsContent value="coincidencias" className="space-y-2">
              {data.coincidencias.length === 0 && <p className="text-sm text-muted-foreground">Sin coincidencias.</p>}
              {data.coincidencias.map((c) => (
                <div key={c.holded.id} className="p-3 border rounded space-y-2">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <div className="text-xs text-muted-foreground">Raven</div>
                      <div className="font-medium">{c.cliente.nombre}</div>
                      <div className="text-xs">{c.cliente.nif_cif}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Holded</div>
                      <div className="font-medium">{c.holded.name}</div>
                      <div className="text-xs">{getHoldedNif(c.holded)}</div>
                    </div>
                  </div>
                  <div className="flex gap-2 text-xs">
                    {(['vincular', 'fusionar', 'ignorar'] as const).map((opt) => (
                      <label key={opt} className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="radio"
                          checked={decisionFusion[c.holded.id] === opt}
                          onChange={() => setDecisionFusion((s) => ({ ...s, [c.holded.id]: opt }))}
                        />
                        {opt === 'vincular' && 'Solo vincular'}
                        {opt === 'fusionar' && 'Vincular y fusionar datos'}
                        {opt === 'ignorar' && 'Ignorar'}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </TabsContent>

            <TabsContent value="raven" className="space-y-1">
              {data.soloRaven.length === 0 && <p className="text-sm text-muted-foreground">Todos tus clientes están en Holded.</p>}
              {data.soloRaven.map((c: any) => (
                <div key={c.id} className="text-sm p-2 border rounded">
                  {c.nombre} — {c.nif_cif || 'sin NIF'}
                </div>
              ))}
            </TabsContent>
          </Tabs>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={aplicar} disabled={aplicando || !data}>
            {aplicando && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Aplicar selección
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
